import type { ExerciseQueueSort } from 'api/rest/exerciseQueues/exerciseQueues.types';
import type { Section } from 'api/rest/sections/sections.types';

import type { OnGoInitializeFormValues } from './onGoInitialize.types';
import { mergePersistedScope, prepareDefaultScope } from './utils';

const STORAGE_KEY = `${process.env.REACT_APP_VERSION ?? 'v1'}_on_go_initialize`;

const SORT_VALUES: ExerciseQueueSort[] = ['section_index', 'random', 'hardest', 'easiest'];

type PersistedOnGoInitStore = Record<string, OnGoInitializeFormValues>;

const readStore = (): PersistedOnGoInitStore => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as PersistedOnGoInitStore;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (store: PersistedOnGoInitStore): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Ignore quota / private mode errors.
  }
};

const sanitizePersistedValues = (raw: OnGoInitializeFormValues, sectionsData: Section[]): OnGoInitializeFormValues => {
  const queueSize = typeof raw.queueSize === 'number' ? Math.trunc(raw.queueSize) : Number(raw.queueSize);
  const sort = SORT_VALUES.includes(raw.sort) ? raw.sort : 'section_index';

  return {
    scope: mergePersistedScope(raw.scope, sectionsData),
    sort,
    unansweredOnly: Boolean(raw.unansweredOnly),
    includeCorrectlyAnswered: Boolean(raw.includeCorrectlyAnswered),
    includeIncorrectlyAnswered: Boolean(raw.includeIncorrectlyAnswered),
    queueSize: Number.isFinite(queueSize) && queueSize >= 1 ? Math.min(100, queueSize) : 5,
    useAllExercises: Boolean(raw.useAllExercises),
  };
};

export const loadOnGoInitializeFormValues = (subjectPk: number, sectionsData: Section[]): OnGoInitializeFormValues | null => {
  const persisted = readStore()[String(subjectPk)];
  if (!persisted) {
    return null;
  }
  return sanitizePersistedValues(persisted, sectionsData);
};

export const saveOnGoInitializeFormValues = (subjectPk: number, values: OnGoInitializeFormValues): void => {
  const store = readStore();
  store[String(subjectPk)] = values;
  writeStore(store);
};

export const resolveOnGoInitializeDefaultValues = (subjectPk: number | undefined, sectionsData: Section[]): OnGoInitializeFormValues => {
  const fallback: OnGoInitializeFormValues = {
    scope: prepareDefaultScope(sectionsData),
    sort: 'section_index',
    unansweredOnly: true,
    includeCorrectlyAnswered: true,
    includeIncorrectlyAnswered: true,
    queueSize: 5,
    useAllExercises: false,
  };

  if (subjectPk == null) {
    return fallback;
  }

  return loadOnGoInitializeFormValues(subjectPk, sectionsData) ?? fallback;
};
